import React, { useState, useEffect } from "react"
import { X, Trash2 } from "lucide-react"

import styles from "./portal-image-viewer.module.scss"

/**
 * Portal image viewer — a large modal that shows a single uploaded screenshot
 * with a "Remove screenshot" action at the bottom. Same white/grey chrome as
 * the upload modal so the portal stays consistent.
 *
 * Props:
 *   image    — { id, public_url, ... } the image doc being viewed
 *   onClose  — () => void
 *   onRemove — async (id) => void (the parent does the delete + log)
 */
export default function PortalImageViewer({ image, onClose, onRemove }) {
	const [removing, setRemoving] = useState(false)
	const [error, setError] = useState("")

	useEffect(() => {
		const onKey = (e) => {
			if (e.key === "Escape" && !removing) onClose()
		}
		window.addEventListener("keydown", onKey)
		const prevOverflow = document.body.style.overflow
		document.body.style.overflow = "hidden"
		return () => {
			window.removeEventListener("keydown", onKey)
			document.body.style.overflow = prevOverflow
		}
	}, [onClose, removing])

	const remove = async () => {
		if (!image?.id) return
		setRemoving(true)
		setError("")
		try {
			await onRemove(image.id)
			// Parent closes the viewer on success.
		} catch (err) {
			setError(err?.message || "Couldn't remove the screenshot. Please try again.")
			setRemoving(false)
		}
	}

	if (!image) return null

	return (
		<div
			className={styles.backdrop}
			onClick={() => !removing && onClose()}>
			<div
				className={styles.modal}
				onClick={(e) => e.stopPropagation()}
				role="dialog"
				aria-modal="true">
				<header className={styles.head}>
					<h3>Flight details</h3>
					<button
						type="button"
						className={styles.close}
						onClick={onClose}
						disabled={removing}
						aria-label="Close">
						<X size={18} />
					</button>
				</header>

				<div className={styles.body}>
					<div className={styles.imageWrap}>
						<img
							src={image.public_url}
							alt="Flight details"
						/>
					</div>
				</div>

				<footer className={styles.footer}>
					{error ? <p className={styles.error}>{error}</p> : null}
					<button
						type="button"
						className={styles.remove}
						onClick={remove}
						disabled={removing}>
						<Trash2 size={16} />
						{removing ? "Removing…" : "Remove screenshot"}
					</button>
				</footer>
			</div>
		</div>
	)
}
